import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Offcanvas } from 'react-bootstrap';
import { set, ref, onValue, get, update } from 'firebase/database';
import { rtdb } from 'customhooks/firebase';
import UnblockConfirmationModal from '../../ConfirmationModals/unblockConfirmationModal/UnblockConfirmationModal';
import BlockConfirmationModal from '../../ConfirmationModals/blockConfirmationModal/BlockConfirmationModal';
import UnmatchConfirmationModal from '../../ConfirmationModals/unmatchConfirmationModal/UnmatchConfirmationModal';
import ReportConfirmationModal from '../../ConfirmationModals/reportConfirmationModal/ReportConfirmationModal';
import dayjs from 'utils/dayjs';
import { apiService } from 'services/apiService';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import ChatBoard from 'components/chat/ChatBoard';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import './QuickChatOffcanvas.css';

interface QuickChatOffcanvasProps {
  show: boolean;
  onHide: () => void;
  candidateId: string;
  candidateName: string;
  candidateImage?: string;
  matchId?: string;
  /** Job this chat is scoped to */
  jobId?: string | number;
  jobTitle?: string;
  /** Optional prop to indicate if the users are connected (for better UX in chat board) */
  connected?: boolean | number;
}

const QuickChatOffcanvas: React.FC<QuickChatOffcanvasProps> = ({
  show,
  onHide,
  candidateId,
  candidateName,
  candidateImage,
  matchId,
  jobId,
  jobTitle,
  connected, // Optional prop to indicate if the users are connected (for better UX in chat board)
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [otherUserOnline, setOtherUserOnline] = useState(false);
  const [blockStatus, setBlockStatus] = useState({ hasBlocked: '0', isBlocked: '0' });

  // ── 3-dot menu state ──
  const [showUnmatchModal, setShowUnmatchModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReasons, setReportReasons] = useState<any[]>([]);
  const [selectedReportReason, setSelectedReportReason] = useState<number | null>(null);
  const [reportDescription, setReportDescription] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [unmatchReason, setUnmatchReason] = useState('');
  const [unmatchReasonErr, setUnmatchReasonErr] = useState(false);
  const [hasInitiatedChatState, setHasInitiatedChatState] = useState(false);

  const currentUserId = userProfile?.user_data?.[0]?.id?.toString() || currentUser?.id || '';
  const currentUserName = userProfile?.user_data?.[0]?.company_name || userProfile?.user_data?.[0]?.full_name || currentUser?.name || 'User';
  const currentUserImage = userProfile?.user_data?.[0]?.logo_image_url || userProfile?.user_data?.[0]?.profile_picture || currentUser?.avatar || '';
  const currentUserEmail = userProfile?.user_data?.[0]?.company_email || userProfile?.user_data?.[0]?.email || currentUser?.email || '';

  // Fetch user profile
  useEffect(() => {
    if (!show) return;
    const fetchUserProfile = async () => {
      try {
        setIsLoadingUser(true);
        const response = await apiService.getAllNewMatches();
        if (response?.status && response?.data) setUserProfile(response.data);
      } catch (error) {
        console.error('[QuickChat] Error fetching user profile:', error);
      } finally {
        setIsLoadingUser(false);
      }
    };
    fetchUserProfile();
  }, [show]);

  // Set online status
  useEffect(() => {
    if (!currentUserId || isLoadingUser || !show) return;
    const userData = {
      id: currentUserId, name: currentUserName, pic: currentUserImage,
      email: currentUserEmail, online: true, lastSeen: Date.now(),
      updatedAt: dayjs().format('DD-MM-YYYY HH:mm:ssZZ'),
    };
    set(ref(rtdb, `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/Users/${currentUserId}`), userData).catch(console.error);
    return () => {
      set(ref(rtdb, `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/Users/${currentUserId}`), { ...userData, online: false }).catch(console.error);
    };
  }, [currentUserId, isLoadingUser, show]);

  // Listen to other user online status
  useEffect(() => {
    if (!candidateId || !show) return;
    const unsub = onValue(ref(rtdb, `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/Users/${candidateId}`), (snap) => {
      setOtherUserOnline(snap.val()?.online === true);
    });
    return () => unsub();
  }, [candidateId, show]);

  // Fetch block status
  useEffect(() => {
    if (!candidateId || !show) return;
    const fetchBlock = async () => {
      try {
        const res = await apiService.getBlockStatus(JSON.stringify({ other_user_id: candidateId }));
        if (res?.status && res.data) {
          setBlockStatus({
            hasBlocked: res.data?.hasBlocked ?? res.data?.has_blocked ?? '0',
            isBlocked: res.data?.isBlocked ?? res.data?.is_blocked ?? '0',
          });
        }
      } catch { /* keep safe default */ }
    };
    fetchBlock();
  }, [candidateId, show]);

  // ── Action handlers ──
  const handleUnmatchClick = async () => {
    if (!currentUserId || !candidateId) return;
    try {
      const snap = await get(ref(rtdb, `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/Inbox/${currentUserId}/${candidateId}`));
      if (snap.exists() && snap.val()?.hasInitiatedChat === true) {
        setHasInitiatedChatState(true);
      } else {
        setHasInitiatedChatState(false);
      }
    } catch (err) {
      console.warn('[QuickChat] Error checking chat status:', err);
      setHasInitiatedChatState(false);
    } finally {
      setShowUnmatchModal(true);
    }
  };

  const handleUnmatchAndDeleteChat = async () => {
    if (!unmatchReason) { setUnmatchReasonErr(true); return; }
    try {
      setActionLoading(true);
      const res = await apiService.unMatchChat({ other_user_id: Number(candidateId), reason: unmatchReason });
      if (res?.status) {
        if (currentUserId && candidateId) {
          try {
            await update(ref(rtdb, `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/Inbox/${currentUserId}/${candidateId}`), { matchStatus: false });
          } catch (fbErr) {
            console.warn('[QuickChat] Firebase update after unmatch failed:', fbErr);
          }
        }
        toast.success('User unmatched successfully');
        setShowUnmatchModal(false);
        setUnmatchReason('');
        setUnmatchReasonErr(false);
        onHide();
      } else {
        toast.error(res?.message || 'Failed to unmatch');
      }
    } catch (e: any) {
      toast.error('Failed to unmatch');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnmatchAndKeepChat = async () => {
    if (!unmatchReason) { setUnmatchReasonErr(true); return; }
    try {
      setActionLoading(true);
      const res = await apiService.unMatchChat({ other_user_id: Number(candidateId), reason: unmatchReason });
      if (res?.status) {
        if (currentUserId && candidateId) {
          try {
            await update(ref(rtdb, `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/Inbox/${currentUserId}/${candidateId}`), { matchStatus: false });
          } catch (fbErr) {
            console.warn('[QuickChat] Firebase update after unmatch failed:', fbErr);
          }
        }
        toast.success('User unmatched successfully');
        setShowUnmatchModal(false);
        setUnmatchReason('');
        setUnmatchReasonErr(false);
        onHide();
      } else {
        toast.error(res?.message || 'Failed to unmatch');
      }
    } catch (e: any) {
      toast.error('Failed to unmatch');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSimpleUnmatch = async () => {
    try {
      setActionLoading(true);
      const res = await apiService.unMatchChat({ other_user_id: Number(candidateId) });
      if (res?.status) {
        toast.success('Unmatched successfully');
        setShowUnmatchModal(false);
        onHide();
      } else {
        toast.error(res?.message || 'Failed to unmatch');
      }
    } catch (e: any) {
      toast.error('Failed to unmatch');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlockToggle = async () => {
    try {
      setActionLoading(true);
      if (blockStatus.hasBlocked === '1') {
        const res = await apiService.unBlockUser({ unblock_user_id: Number(candidateId) });
        if (res?.status) {
          toast.success('User unblocked');
          setBlockStatus((prev) => ({ ...prev, hasBlocked: '0' }));
          setShowBlockModal(false);
        } else {
          toast.error(res?.message || 'Failed to unblock');
        }
      } else {
        const res = await apiService.blockUser({ block_user_id: Number(candidateId) });
        if (res?.status) {
          toast.success('User blocked');
          setBlockStatus((prev) => ({ ...prev, hasBlocked: '1' }));
          setShowBlockModal(false);
        } else {
          toast.error(res?.message || 'Failed to block');
        }
      }
    } catch (e: any) {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const openReportModal = async () => {
    try {
      const res = await apiService.getReportReasonList();
      const list = res?.data || res?.reasons || [];
      setReportReasons(Array.isArray(list) ? list : []);
    } catch {
      setReportReasons([]);
    }
    setSelectedReportReason(null);
    setReportDescription('');
    setShowReportModal(true);
  };

  const handleReport = async () => {
    if (!selectedReportReason) {
      toast.error('Please select a reason');
      return;
    }
    try {
      setActionLoading(true);
      const res = await apiService.chatReport({
        report_user_id: Number(candidateId),
        report_reason_id: selectedReportReason,
        description: reportDescription,
      });
      if (res?.status) {
        toast.success('Report submitted successfully');
        setShowReportModal(false);
      } else {
        toast.error(res?.message || 'Failed to submit report');
      }
    } catch (e: any) {
      toast.error('Failed to submit report');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Offcanvas show={show} onHide={onHide} placement="end" className="quick-chat-offcanvas">
        <Offcanvas.Body className="p-0" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', minHeight: 0 }}>
          {!isLoadingUser && currentUserId ? (
            <ChatBoard
              otherUserId={candidateId}
              otherUserName={candidateName}
              otherUserImage={candidateImage}
              otherUserOnline={otherUserOnline}
              matchId={matchId}
              matchStatus={true}
              blockStatus={blockStatus}
              currentUserId={currentUserId}
              currentUserName={currentUserName}
              currentUserImage={currentUserImage}
              currentUserEmail={currentUserEmail}
              jobId={jobId ? String(jobId) : undefined}
              jobTitle={jobTitle}
              onUnmatch={handleUnmatchClick}
              onBlock={() => setShowBlockModal(true)}
              onReport={openReportModal}
              connected={connected}
              onClose={onHide}
            />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff' }}>
            {/* Header skeleton */}
            <div style={{ padding: '20px 25px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className='d-flex align-items-center gap-3'>
                <Skeleton circle width={60} height={60} />
                <div style={{ flex: 1 }}>
                  <Skeleton width={140} height={18} style={{ marginBottom: 8 }} />
                  <Skeleton width={60} height={14} />
                </div>
              </div>
              <div
                onClick={onHide}
                style={{ fontSize: 28,background: 'transparent !important', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <svg className="ChatMore_Feature_Btn" width="1em" height="1em" viewBox="0 0 34 34" fill="none">
                  <path d="M0 6C0 2.68629 2.68629 0 6 0H28C31.3137 0 34 2.68629 34 6V28C34 31.3137 31.3137 34 28 34H6C2.68629 34 0 31.3137 0 28V6Z" fill="#457EFF" fillOpacity="0.06" />
                  <path d="M6 0.5H28C31.0376 0.5 33.5 2.96243 33.5 6V28C33.5 31.0376 31.0376 33.5 28 33.5H6C2.96243 33.5 0.5 31.0376 0.5 28V6C0.5 2.96243 2.96243 0.5 6 0.5Z" stroke="#457EFF" strokeOpacity="0.6" />
                  <path d="M12 12L22 22M22 12L12 22" stroke="#457EFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            {/* Messages skeleton */}
            <div style={{ flex: 1, padding: '25px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto' }}>
              {/* Other user message */}
              <div className='d-flex align-items-end gap-2'>
                <Skeleton circle width={34} height={34} style={{ flexShrink: 0 }} />
                <div>
                  <Skeleton width={180} height={14} style={{ marginBottom: 6 }} />
                  <Skeleton width={220} height={50} style={{ borderRadius: 12 }} />
                </div>
              </div>

              {/* Current user message */}
              <div className='d-flex justify-content-end'>
                <Skeleton width={200} height={50} style={{ borderRadius: 12 }} />
              </div>

              {/* Other user message */}
              <div className='d-flex align-items-end gap-2'>
                <Skeleton circle width={34} height={34} style={{ flexShrink: 0 }} />
                <div>
                  <Skeleton width={180} height={14} style={{ marginBottom: 6 }} />
                  <Skeleton width={260} height={50} style={{ borderRadius: 12 }} />
                </div>
              </div>

              {/* Current user message */}
              <div className='d-flex justify-content-end'>
                <Skeleton width={170} height={50} style={{ borderRadius: 12 }} />
              </div>

              {/* Other user message */}
              <div className='d-flex align-items-end gap-2'>
                <Skeleton circle width={34} height={34} style={{ flexShrink: 0 }} />
                <div>
                  <Skeleton width={180} height={14} style={{ marginBottom: 6 }} />
                  <Skeleton width={190} height={50} style={{ borderRadius: 12 }} />
                </div>
              </div>
            </div>

            {/* Input skeleton */}
            <div style={{ padding: '15px 25px', borderTop: '1px solid #f0f0f0' }}>
              <div className='d-flex gap-2 align-items-center'>
                <Skeleton width="100%" height={50} style={{ borderRadius: 6 }} />
                <Skeleton circle width={50} height={50} />
              </div>
            </div>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>

      {hasInitiatedChatState ? (
        <UnmatchConfirmationModal
          show={showUnmatchModal}
          onHide={() => { setShowUnmatchModal(false); setUnmatchReason(''); setUnmatchReasonErr(false); }}
          onConfirm={handleUnmatchAndDeleteChat}
          onConfirmKeep={handleUnmatchAndKeepChat}
          userName={candidateName}
          title={`Are you sure you want to un-match ${candidateName}?`}
          buttonText="Unmatch & Delete Chat"
          secondButtonText='Unmatch & Keep Chat'
          unmatchReason={unmatchReason}
          setUnmatchReason={setUnmatchReason}
          unmatchReasonErr={unmatchReasonErr}
          setUnmatchReasonErr={setUnmatchReasonErr}
          isUnmatching={actionLoading}
        />
      ) : (
        <UnmatchConfirmationModal
          show={showUnmatchModal}
          onHide={() => setShowUnmatchModal(false)}
          onConfirm={handleSimpleUnmatch}
          userName={candidateName}
          isUnmatching={actionLoading}
        />
      )}

      {/* Block / Unblock Modal */}
      {blockStatus.hasBlocked === '1' ? (
        <UnblockConfirmationModal
          show={showBlockModal}
          onHide={() => setShowBlockModal(false)}
          onConfirm={handleBlockToggle}
          userName={candidateName}
          isUnblocking={actionLoading}
        />
      ) : (
        <BlockConfirmationModal
          show={showBlockModal}
          onHide={() => setShowBlockModal(false)}
          onConfirm={handleBlockToggle}
          userName={candidateName}
          isBlocking={actionLoading}
        />
      )}

      {/* Report Modal */}
      <ReportConfirmationModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        onSubmit={async (reasonId, desc) => {
          setSelectedReportReason(Number(reasonId));
          setReportDescription(desc);
          try {
            setActionLoading(true);
            const res = await apiService.chatReport({
              report_user_id: Number(candidateId),
              report_reason_id: reasonId,
              description: desc,
            });
            if (res?.status) {
              toast.success("User reported successfully");
              setShowReportModal(false);
            } else {
              toast.error(res?.message || "Failed to submit report");
            }
          } catch (error: any) {
            console.error("Report failed:", error?.response?.data || error?.message || error);
            toast.error("Failed to submit report");
          } finally {
            setActionLoading(false);
          }
        }}
        userName={candidateName}
        reasons={reportReasons}
        isSubmitting={actionLoading}
      />
    </>
  );
};

export default QuickChatOffcanvas;
